import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNetworkStatus } from '@/api'
import type { MessageKey } from '@/i18n'
import { useActor } from '@/auth/pin/phoneSession'
import { downloadCaches, getDraft } from '@/offline'
import { Btn, Cta, Footer, ItemRow, Row, Strip, V1Screen } from '@/features/gate/v1/kit'
import { tr } from '@/features/gate/v1/text'
import { C } from '@/features/gate/v1/style'
import type { StoreState } from '../projection'
import { useNow } from '../useStore'
import {
  agoText,
  IconBox,
  RefreshButton,
  StaleStrip,
  StoreHeader,
  VehicleRow,
  WithStore,
} from './bits'
import { SIcon, type StoreIconName } from './icons'
import {
  arrivingTrips,
  dockBags,
  throttledRunner,
  tripBags,
  tripWhich,
  type HeldGroup,
} from './logic'
import { heldDraftId } from './Receive'

/**
 * The cache download on opening the godown home, coming back to it, and going online: one at a
 * time and at most every 30 s, failures silent (offline keeps the list this phone has).
 */
const freshen = throttledRunner(() => downloadCaches(), 30_000)

/** The godown's other jobs, below the vehicles on the way. */
const MORE: Array<{ to: string; icon: StoreIconName; title: MessageKey; line: MessageKey }> = [
  {
    to: '/m/store/move',
    icon: 'rowbags',
    title: 'store.v1.more.move',
    line: 'store.v1.more.moveLine',
  },
  {
    to: '/m/store/count',
    icon: 'list',
    title: 'store.v1.more.count',
    line: 'store.v1.more.countLine',
  },
  {
    to: '/m/store/stock',
    icon: 'search',
    title: 'store.v1.more.stock',
    line: 'store.v1.more.stockLine',
  },
  {
    to: '/m/store/red-tie',
    icon: 'redtie',
    title: 'store.v1.more.redTie',
    line: 'store.v1.more.redTieLine',
  },
  {
    to: '/m/store/replace',
    icon: 'problembag',
    title: 'store.v1.more.replace',
    line: 'store.v1.more.replaceLine',
  },
]

/** At a processing site the keeper also gives bags from the godown to the run. */
const ISSUE: typeof MORE = [
  {
    to: '/m/process/issue',
    icon: 'sack',
    title: 'store.v1.more.issue',
    line: 'store.v1.more.issueLine',
  },
]

/** Put away, in the other jobs while no bag waits on the dock (else it shows at the top). */
const PUT_AWAY: typeof MORE = [
  {
    to: '/m/store/put-away',
    icon: 'rowbags',
    title: 'store.v1.more.putAway',
    line: 'store.v1.more.putAwayLine',
  },
]

/** V1-F5-00a — गोदाम · आज: the vehicles on their way here, the godown form, माल उतारें. */
export function V1GodownHome() {
  const navigate = useNavigate()
  // No store list yet and offline: अधिक is the way out (the phone's settings, sign out).
  return (
    <WithStore onBack={() => navigate('/m/more')}>{(state) => <Home state={state} />}</WithStore>
  )
}

function Home({ state }: { state: StoreState }) {
  const navigate = useNavigate()
  const actor = useActor()
  const now = useNow()
  const trips = arrivingTrips(state)
  // A truck sent here since the last download shows without "update the list". Offline or a
  // failed download keeps the list this phone has.
  const { online } = useNetworkStatus()
  useEffect(() => {
    if (!online) return
    void freshen()
    const onShow = () => {
      if (document.visibilityState === 'visible') void freshen()
    }
    document.addEventListener('visibilitychange', onShow)
    return () => document.removeEventListener('visibilitychange', onShow)
  }, [online])
  const dock = useMemo(() => dockBags(state).length, [state])
  const held = useLiveQuery(
    async () => (await getDraft<{ groups: HeldGroup[] }>(heldDraftId(state.siteId)))?.data.groups,
    [state.siteId],
  )
  return (
    <V1Screen
      nav
      header={
        <StoreHeader
          // At the packing hall these boards open from the hall home: the way back to it.
          onBack={
            state.cache.config['site_kind'] === 'PACKING'
              ? () => navigate('/m/pack/hall')
              : undefined
          }
          icon="home"
          title={tr('store.v1.home.title', { site: state.cache.site_name ?? '' })}
          sub={actor.name}
        />
      }
      footer={
        <Footer>
          <Row>
            <Btn onClick={() => navigate('/m/store/declaration')}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <SIcon name="shield" size={22} />
                {tr('store.v1.home.form')}
              </span>
            </Btn>
            {/* A trip from this godown to another site (processing, packing, another godown). */}
            <Btn onClick={() => navigate('/m/store/transfer')}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <SIcon name="godown" size={22} />
                {tr('store.v1.home.send')}
              </span>
            </Btn>
          </Row>
          <Cta
            label={tr('store.v1.home.receive')}
            icon="truck"
            lead
            onClick={() => navigate('/m/store/receive')}
          />
        </Footer>
      }
    >
      <StaleStrip state={state} />
      {/* Bags unloaded at the dock go to their place in the godown: first thing on the page. */}
      {dock > 0 && (
        <ItemRow
          onClick={() => navigate('/m/store/put-away')}
          padding="8px 10px"
          border={C.acc}
          borderWidth={2}
          background={C.warnWash}
          lead={
            <IconBox
              icon="rowbags"
              size={44}
              iconSize={26}
              background={C.surface}
              color={C.accDark}
            />
          }
          title={tr('store.v1.home.dock', { count: dock })}
          line={
            <span style={{ fontSize: 15, color: C.muted }}>{tr('store.v1.home.dockLine')}</span>
          }
          right={
            <span style={{ color: C.accDark, display: 'flex' }}>
              <SIcon name="next" size={24} sw={2.5} />
            </span>
          }
        />
      )}
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
        {tr('store.v1.home.arriving')}
      </h2>
      {trips.map((t, i) => (
        <VehicleRow
          key={t.shipment_id}
          first={i === 0}
          chevron
          vehicle={t.vehicle_no ?? t.code}
          line={tr('store.v1.tripLine', { village: t.from_name ?? '', count: tripBags(t) })}
          extra={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <SIcon name="clock" size={15} />
              {tripWhich(t, now)}
              {t.dispatched_at ? ` · ${agoText(t.dispatched_at, now)}` : ''}
            </span>
          }
          onClick={() => navigate(`/m/store/receive?trip=${encodeURIComponent(t.shipment_id)}`)}
        />
      ))}
      {trips.length === 0 && (
        <>
          <Strip icon="truck">{tr('store.v1.home.none')}</Strip>
          <Row>
            <RefreshButton />
          </Row>
        </>
      )}
      {(held ?? [])
        .filter((g) => g.scans.length > 0)
        .map((g) => (
          <div
            key={g.digits}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              borderRadius: 12,
              background: C.warnWash,
              fontSize: 16,
            }}
          >
            <SIcon name="hourglass" size={22} style={{ color: C.accDark }} />
            {tr('store.v1.home.held', { digits: g.digits, count: g.scans.length })}
          </div>
        ))}
      <h2 style={{ margin: '6px 0 0', fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
        {tr('store.v1.more.title')}
      </h2>
      {[
        ...(state.cache.config['site_kind'] === 'PROCESSING' ? ISSUE : []),
        ...(dock === 0 ? PUT_AWAY : []),
        ...MORE,
      ].map((m) => (
        <ItemRow
          key={m.to}
          onClick={() => navigate(m.to)}
          padding="8px 10px"
          border={C.border}
          lead={<IconBox icon={m.icon} size={44} iconSize={26} />}
          title={tr(m.title)}
          line={<span style={{ fontSize: 15, color: C.muted }}>{tr(m.line)}</span>}
          right={
            <span style={{ color: C.acc, display: 'flex' }}>
              <SIcon name="next" size={24} sw={2.5} />
            </span>
          }
        />
      ))}
    </V1Screen>
  )
}
